import React from 'react'
import {
  IconCheckDefault,
  IconCloseDefault,
  IconInfoDefault,
} from '@mc/design-system'
import { Trans, useTranslation } from '@mc/i18n'
import { TableFeature } from '@mc/plans-ui'
import { trackCertificateRowClick } from '../../../events/pageClick'
import { trackCertificatesRowViewed } from '../../../events/rowViewed'
import { useCertificatePriceLimit } from '../../../hooks/useCertificatePriceLimit'
import { BUNDLING_TOOLKIT_AND_CERTIFICATES_I18N_NAMESPACE } from '../../../i18n'
import { FAQ_UUID } from '../../FAQ'

export const CertificateFeature = ({
  titleI18nKey = 'matrix.features.certificate.title',
  duoIncluded = false,
}: {
  duoIncluded?: boolean
  titleI18nKey?: string
}) => {
  const { t } = useTranslation(BUNDLING_TOOLKIT_AND_CERTIFICATES_I18N_NAMESPACE)
  const maxPrice = useCertificatePriceLimit()

  const handleInfoClick = (source: 'icon' | 'link') => () => {
    trackCertificateRowClick({ source })

    if (typeof window === 'undefined') return

    const heading = window.document.querySelector<HTMLElement>(
      `[data-uuid="${FAQ_UUID}"]`,
    )
    if (!heading) return

    heading.scrollIntoView({ behavior: 'smooth' })

    const button = heading.querySelector<HTMLButtonElement>(
      'button[aria-expanded="false"]',
    )
    if (!button) return

    button.focus()
    button.click()
  }

  return (
    <TableFeature.Feature onFirstVisible={trackCertificatesRowViewed}>
      <TableFeature.Label
        title={
          <span className='d-flex align-items-center flex-gap-1'>
            <Trans
              ns={BUNDLING_TOOLKIT_AND_CERTIFICATES_I18N_NAMESPACE}
              i18nKey={titleI18nKey}
            />
            <button
              style={{ lineHeight: '0' }}
              className='mc-clickable'
              onClick={handleInfoClick('icon')}
            >
              <IconInfoDefault
                size={2}
                srText={t('matrix.features.common.info')}
              />
            </button>
          </span>
        }
        subtitle={
          <Trans
            ns={BUNDLING_TOOLKIT_AND_CERTIFICATES_I18N_NAMESPACE}
            i18nKey='matrix.features.certificate.subtitle'
            values={{ maxPrice }}
            components={{
              noWrap: <span className='mc-text--nowrap' />,
              learnMoreLink: (
                <button
                  className='mc-text--link d-md-block mc-m-auto'
                  onClick={handleInfoClick('link')}
                />
              ),
            }}
          />
        }
      />
      <TableFeature.Individual>
        <IconCloseDefault
          size={2}
          srText={t('matrix.features.common.notIncluded')}
        />
      </TableFeature.Individual>
      <TableFeature.Duo>
        {duoIncluded ? (
          <IconCheckDefault
            size={2}
            srText={t('matrix.features.common.included')}
          />
        ) : (
          <IconCloseDefault
            size={2}
            srText={t('matrix.features.common.notIncluded')}
          />
        )}
      </TableFeature.Duo>
      <TableFeature.Family>
        <IconCheckDefault
          size={2}
          srText={t('matrix.features.common.included')}
        />
      </TableFeature.Family>
    </TableFeature.Feature>
  )
}
